<template>
  <div class="resource-card-wrapper">
    <v-card
      v-bind="$attrs"
      class="ns-card resource-card"
      :class="{ 'uploading': isUploading, 'upload-failed': isUploadFailed }"
      :elevation="0"
      @click="openResource"
    >
      <div class="resource-card-inner">
        <img
          v-if="resourceType(resource) == 'pdf'"
          class="resource-icon"
          src="../../assets/images/pdf.png"
          alt="pdf"
        >
        <img
          v-if="resourceType(resource) == 'url'"
          class="resource-icon"
          src="../../assets/images/url.png"
          alt="url"
        >
        <img
          v-if="resourceType(resource) == 'docx'"
          class="resource-icon"
          src="../../assets/images/doc.png"
          alt="docx"
        >
        <img
          v-if="resourceType(resource) == 'txt'"
          class="resource-icon"
          src="../../assets/images/txt.png"
          alt="txt"
        >
        <img
          v-if="resourceType(resource) == 'img'"
          class="resource-icon"
          src="../../assets/images/img.png"
          alt="image"
        >
        <img
          v-if="!['docx', 'url', 'pdf', 'txt', 'img'].includes(resourceType(resource))"
          class="resource-icon"
          src="../../assets/images/document.png"
          alt="document"
        >
        <div class="resource-title card-title-ellipsis">
          {{ resourceTitle(resource) }}
        </div>
      </div>
      <span
        v-if="isUploading"
        class="upload-status-badge"
        :class="uploadBadgeClass"
      >
        {{ uploadStatusLabel }}
      </span>
    </v-card>
    <Button
      v-if="canDelete"
      class="delete-btn"
      icon="pi pi-trash"
      severity="danger"
      text
      rounded
      size="small"
      aria-label="Delete resource"
      data-test="delete-resource-btn"
      @click.stop="deleteDialogOpen = true"
    />
    <Dialog
      v-model:visible="deleteDialogOpen"
      modal
      :header="RESOURCE_DELETE_TITLE"
      :style="{ width: '400px' }"
    >
      <p class="delete-warning">
        {{ RESOURCE_DELETE_WARNING_MSG }}
      </p>
      <p
        v-if="deleteError"
        class="delete-error"
      >
        {{ deleteError }}
      </p>
      <template #footer>
        <Button
          label="Cancel"
          text
          :disabled="isDeleting"
          @click="deleteDialogOpen = false"
        />
        <Button
          label="Delete"
          severity="danger"
          :loading="isDeleting"
          data-test="confirm-delete-resource-btn"
          @click="confirmDelete"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
  import Button from 'primevue/button';
  import Dialog from 'primevue/dialog';
  import { 
    RESOURCE_TYPE_URL, 
    RESOURCE_DELETE_TITLE,
    RESOURCE_DELETE_WARNING_MSG,
    RESOURCE_DELETE_ERROR_MSG,
    RESOURCE_UPLOAD_STATUS_COMPLETED, 
    RESOURCE_UPLOAD_STATUS_START_SUCCESS, 
    RESOURCE_UPLOAD_STATUS_START_FAILED, 
    RESOURCE_UPLOAD_STATUS_OCR_PARSING, 
    RESOURCE_UPLOAD_STATUS_CONTENT_PARSING, 
    RESOURCE_STATUS_GENERATION_IN_PROGRESS, 
    RESOURCE_STATUS_GENERATION_PARSING, 
    RESOURCE_STATUS_GENERATION_COMPLETED, 
    RESOURCE_STATUS_GENERATION_FAILED 
  } from "@/assets/constants/constants.js";
  import { getUrlShortReference } from "@/utils/utils.js";
  import { StorageService } from "@/services/StorageService";
  import { deleteResource } from "@/services/knowledgebaseService";

  export default {
    name: "ResourceCard",
    components: { Button, Dialog },
    inheritAttrs: false,
    props: {
      resource: {
        type: Object,
        default: null
      }
    },
    emits: ['deleted'],
    data() {
      return {
        deleteDialogOpen: false,
        isDeleting: false,
        deleteError: '',
        RESOURCE_DELETE_TITLE,
        RESOURCE_DELETE_WARNING_MSG
      };
    },
    computed: {
      canDelete() {
        return !this.isUploading || this.isUploadFailed;
      },
      isUploading() {
        const status = this.resource?.upload_status;
        if (!status) return false;
        const doneStatuses = [RESOURCE_UPLOAD_STATUS_COMPLETED, RESOURCE_STATUS_GENERATION_COMPLETED];
        return !doneStatuses.includes(status);
      },
      isUploadFailed() {
        return this.resource?.upload_status === RESOURCE_UPLOAD_STATUS_START_FAILED ||
               this.resource?.upload_status === RESOURCE_STATUS_GENERATION_FAILED;
      },
      uploadStatusLabel() {
        const status = this.resource?.upload_status;
        if (status === RESOURCE_UPLOAD_STATUS_START_SUCCESS) return 'Uploading...';
        if (status === RESOURCE_UPLOAD_STATUS_START_FAILED) return 'Failed';
        if (status === RESOURCE_UPLOAD_STATUS_OCR_PARSING) return 'OCR...';
        if (status === RESOURCE_UPLOAD_STATUS_CONTENT_PARSING) return 'Parsing...';
        if (status === RESOURCE_STATUS_GENERATION_IN_PROGRESS) return 'Generating...';
        if (status === RESOURCE_STATUS_GENERATION_PARSING) return 'Parsing...';
        if (status === RESOURCE_STATUS_GENERATION_FAILED) return 'Failed';
        return 'Processing...';
      },
      uploadBadgeClass() {
        return this.isUploadFailed ? 'badge-danger' : 'badge-warn';
      },
    },
    methods: {
      async confirmDelete() {
        this.isDeleting = true;
        this.deleteError = '';
        try {
          await deleteResource(this.resource.id);
          this.deleteDialogOpen = false;
          this.$emit('deleted', this.resource.id);
        } catch (error) {
          this.deleteError = RESOURCE_DELETE_ERROR_MSG;
        } finally {
          this.isDeleting = false;
        }
      },
      openResource() {
        if (this.isUploading) return;
        this.$store.state.readerDocument = this.resource;
        StorageService.saveResourceContext(this.resource);
        this.$router.push({ name: "ResourceReader"});
      },
      resourceTitle(resource){
        if (resource.resource_type == RESOURCE_TYPE_URL) {
          return getUrlShortReference(resource.resource_url)
        } else {
          return resource.resource_name;
        }
      },
      resourceType(resource){
        return resource.resource_type 
      },
    }
}
</script>

<style scoped>
.resource-card-wrapper {
  position: relative;
  width: 100px;
  height: 90px;
}

.resource-card {
  width: 100px;
  height: 90px;
  padding: var(--space-2) !important;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.resource-card.uploading {
  opacity: 0.6;
  pointer-events: none;
}

.resource-card-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  width: 100%;
}

.resource-icon {
  width: 30px;
  height: 30px;
}

.resource-title {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  text-align: center;
  color: var(--color-text-primary);
}

.card-title-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  white-space: nowrap;
}

.upload-status-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 2px 4px;
  font-size: var(--font-size-xs);
  border-radius: var(--radius-sm) 0 var(--radius-sm) 0;
  font-weight: var(--font-weight-bold);
  line-height: 1.2;
}

.upload-status-badge.badge-warn {
  background-color: var(--color-warning-light);
  color: var(--color-warning);
}

.upload-status-badge.badge-danger {
  background-color: var(--color-error-light);
  color: var(--color-error);
}

.delete-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 1.75rem !important;
  height: 1.75rem !important;
  background-color: var(--color-background-secondary) !important;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.resource-card-wrapper:hover .delete-btn,
.delete-btn:focus-visible {
  opacity: 1;
}

@media (hover: none) {
  .delete-btn {
    opacity: 1;
  }
}

.delete-warning {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: var(--line-height-base);
}

.delete-error {
  margin: var(--space-3) 0 0;
  color: var(--color-error);
}
</style>